import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useSiteContent, SiteContentItem } from '@/hooks/useSiteContent'
import { lumi } from '@/lib/lumi'
import {Save, Trash2, LogOut, FileText, ArrowLeft, Search, Eye, EyeOff, CheckCircle2, Circle, Sparkles, Home as HomeIcon, Compass, Wind, RefreshCw, AlignLeft, Heading, MousePointerClick, Type, ExternalLink, Palette} from 'lucide-react'

const PAGES = [
  { key: 'accueil', label: 'Accueil', icon: HomeIcon, color: '#c9a96e' },
  { key: 'comprendre', label: 'Comprendre', icon: Compass, color: '#c9a96e' },
  { key: 'experimenter', label: 'Expérimenter', icon: Sparkles, color: '#c9a96e' },
  { key: 'transformation', label: 'Transformation', icon: Wind, color: '#c9a96e' },
  { key: 'alignement', label: 'Alignement', icon: Circle, color: '#c9a96e' },
  { key: 'accompagnements', label: 'Accompagnements', icon: FileText, color: '#c9a96e' },
  { key: 'sanctuaire', label: 'Sanctuaire', icon: Palette, color: '#c9a96e' },
  { key: 'contact', label: 'Contact', icon: FileText, color: '#c9a96e' },
]

const CONTENT_TYPE_ICONS: Record<string, any> = {
  title: Heading,
  subtitle: Type,
  paragraph: AlignLeft,
  button: MousePointerClick,
  list: FileText,
  card: FileText,
}

const SECTION_TEMPLATES: Record<string, Array<{ sectionKey: string; label: string; contentType: SiteContentItem['contentType']; fallback: string }>> = {
  accueil: [
    { sectionKey: 'hero_eyebrow', label: 'Eyebrow (haut)', contentType: 'subtitle', fallback: '— Un espace intérieur —' },
    { sectionKey: 'hero_title_1', label: 'Titre principal (ligne 1)', contentType: 'title', fallback: 'Bienvenue dans le laboratoire' },
    { sectionKey: 'hero_title_2', label: 'Titre principal (ligne 2)', contentType: 'title', fallback: 'de transformation.' },
    { sectionKey: 'hero_quote', label: 'Citation italique', contentType: 'paragraph', fallback: "Ici, rien n'est à comprendre avec l'esprit seul." },
    { sectionKey: 'hero_desc', label: 'Description', contentType: 'paragraph', fallback: "Dépose ce que tu sais. Laisse venir ce qui se présente." },
    { sectionKey: 'baseline', label: 'Baseline (section 3 portes)', contentType: 'subtitle', fallback: 'Comprendre. Ressentir. Transformer.' },
    { sectionKey: 'invitation_quote', label: "Citation d'invitation", contentType: 'paragraph', fallback: 'On peut observer que tout ce qui nous traverse laisse une empreinte dans le corps, dans la perception, dans le silence.' },
    { sectionKey: 'invitation_text', label: "Texte d'invitation", contentType: 'paragraph', fallback: "Ce laboratoire n'impose rien. Il propose des pistes, des expériences, des espaces où poser son attention." },
    { sectionKey: 'invitation_cta', label: "Bouton d'invitation", contentType: 'button', fallback: "Entrer dans l'expérience" },
  ],
  comprendre: [
    { sectionKey: 'page_title', label: 'Titre de la page', contentType: 'title', fallback: 'Comprendre' },
    { sectionKey: 'page_intro', label: 'Introduction', contentType: 'paragraph', fallback: 'Des pistes de compréhension, jamais des vérités absolues.' },
  ],
  experimenter: [
    { sectionKey: 'page_title', label: 'Titre de la page', contentType: 'title', fallback: 'Expérimenter' },
    { sectionKey: 'page_intro', label: 'Introduction', contentType: 'paragraph', fallback: 'Un espace pour ressentir, écouter, se laisser traverser.' },
  ],
  transformation: [
    { sectionKey: 'page_title', label: 'Titre de la page', contentType: 'title', fallback: 'Transformation' },
    { sectionKey: 'page_intro', label: 'Introduction', contentType: 'paragraph', fallback: 'Des parcours simples pour accompagner les états intérieurs.' },
  ],
  alignement: [
    { sectionKey: 'page_title', label: 'Titre de la page', contentType: 'title', fallback: 'Alignement' },
    { sectionKey: 'page_intro', label: 'Introduction', contentType: 'paragraph', fallback: "La cohérence intérieure, l'écoute du corps, les choix alignés." },
  ],
  accompagnements: [
    { sectionKey: 'hero_eyebrow', label: 'Eyebrow (haut)', contentType: 'subtitle', fallback: '— Temps partagés —' },
    { sectionKey: 'hero_title', label: 'Titre principal', contentType: 'title', fallback: 'Accompagnements' },
    { sectionKey: 'hero_subtitle', label: 'Sous-titre', contentType: 'paragraph', fallback: 'Des espaces dédiés,\npour traverser ensemble ce qui demande du temps.' },
    { sectionKey: 'loading_text', label: 'Texte de chargement', contentType: 'paragraph', fallback: 'Chargement des accompagnements…' },
    { sectionKey: 'empty_text', label: 'Texte liste vide', contentType: 'paragraph', fallback: 'Aucun accompagnement proposé pour le moment.' },
    { sectionKey: 'prix_from_label', label: 'Libellé « À partir de »', contentType: 'subtitle', fallback: 'À partir de' },
    { sectionKey: 'prix_sur_demande', label: 'Libellé tarif sur demande', contentType: 'subtitle', fallback: 'Tarif sur demande' },
    { sectionKey: 'prochaines_dates_label', label: 'Libellé prochaines dates', contentType: 'subtitle', fallback: 'Prochaines dates' },
    { sectionKey: 'pour_se_preparer_label', label: 'Libellé préparation', contentType: 'subtitle', fallback: 'Pour se préparer' },
    { sectionKey: 'btn_reserver', label: 'Bouton « Réserver »', contentType: 'button', fallback: 'Réserver un temps' },
    { sectionKey: 'btn_prendre_contact', label: 'Bouton « Prendre contact »', contentType: 'button', fallback: 'Prendre contact' },
    { sectionKey: 'btn_organiser_chez_vous', label: 'Bouton « Organiser chez vous »', contentType: 'button', fallback: 'Organiser chez vous' },
    { sectionKey: 'organiser_eyebrow', label: 'Bloc organisateur — Eyebrow', contentType: 'subtitle', fallback: 'Vous êtes organisateur' },
    { sectionKey: 'organiser_title_1', label: 'Bloc organisateur — Titre ligne 1', contentType: 'title', fallback: 'Accueillir un accompagnement' },
    { sectionKey: 'organiser_title_2', label: 'Bloc organisateur — Titre ligne 2', contentType: 'title', fallback: 'chez vous' },
    { sectionKey: 'organiser_text_1', label: 'Bloc organisateur — Texte 1', contentType: 'paragraph', fallback: 'Centre, studio, lieu de pratique, collectif — si vous souhaitez proposer\nun de ces accompagnements à votre communauté, nous pouvons venir à vous.' },
    { sectionKey: 'organiser_text_2', label: 'Bloc organisateur — Texte 2', contentType: 'paragraph', fallback: "Échangeons sur votre lieu, votre public, le format et les conditions d'organisation." },
    { sectionKey: 'organiser_cta', label: 'Bloc organisateur — Bouton', contentType: 'button', fallback: 'Proposer mon lieu' },
    { sectionKey: 'paiement_eyebrow', label: 'Paiement — Eyebrow', contentType: 'subtitle', fallback: 'Modalités' },
    { sectionKey: 'paiement_title', label: 'Paiement — Titre', contentType: 'title', fallback: 'Paiement en ligne sécurisé' },
    { sectionKey: 'paiement_text', label: 'Paiement — Texte', contentType: 'paragraph', fallback: "La réservation se fait à travers un échange préalable, pour s'assurer que l'accompagnement correspond à ce qui est cherché. Le règlement peut se faire en une ou plusieurs fois, selon ce qui est juste pour toi." },
    { sectionKey: 'paiement_mode_1', label: 'Mode de paiement 1', contentType: 'subtitle', fallback: 'Carte bancaire' },
    { sectionKey: 'paiement_mode_2', label: 'Mode de paiement 2', contentType: 'subtitle', fallback: 'Virement' },
    { sectionKey: 'paiement_mode_3', label: 'Mode de paiement 3', contentType: 'subtitle', fallback: 'Paiement en plusieurs fois' },
    { sectionKey: 'note_disclaimer', label: 'Note finale (disclaimer)', contentType: 'paragraph', fallback: "Ces accompagnements ne remplacent pas un suivi médical ou psychologique.\nIls proposent un espace d'exploration personnelle, jamais une promesse de guérison." },
  ],
  sanctuaire: [
    { sectionKey: 'hero_eyebrow', label: 'Hero — Eyebrow', contentType: 'subtitle', fallback: '— Le Sanctuaire —' },
    { sectionKey: 'hero_title_1', label: 'Hero — Titre ligne 1', contentType: 'title', fallback: "L'art qui transforme" },
    { sectionKey: 'hero_title_2', label: 'Hero — Titre ligne 2 (italique)', contentType: 'title', fallback: "de l'intérieur." },
    { sectionKey: 'hero_manifeste', label: 'Hero — Manifeste', contentType: 'paragraph', fallback: 'Entrer.\nRessentir.\nSe transformer.' },
    { sectionKey: 'hero_desc', label: 'Hero — Description', contentType: 'paragraph', fallback: 'Un lieu signature. Discret, structuré, esthétique, artistique et maîtrisé.\nUne activité exclusivement sur rendez-vous.' },
    { sectionKey: 'position_eyebrow', label: 'Positionnement — Eyebrow', contentType: 'subtitle', fallback: 'Positionnement' },
    { sectionKey: 'position_quote', label: 'Positionnement — Citation', contentType: 'paragraph', fallback: "Un espace réel, structuré, dédié à l'accueil d'expériences artistiques et esthétiques." },
    { sectionKey: 'position_text', label: 'Positionnement — Texte', contentType: 'paragraph', fallback: "Un lieu où l'exigence, la qualité et la maîtrise permettent une expérience sensible et ancrée.\nL'esthétique, l'art et le bien-être se complètent — sans nuisance, sans passage libre." },
    { sectionKey: 'espaces_eyebrow', label: 'Espaces — Eyebrow', contentType: 'subtitle', fallback: 'Organisation des espaces' },
    { sectionKey: 'espaces_title_1', label: 'Espaces — Titre ligne 1', contentType: 'title', fallback: 'Trois' },
    { sectionKey: 'espaces_title_2', label: 'Espaces — Titre ligne 2 (italique)', contentType: 'title', fallback: 'territoires' },
    { sectionKey: 'espace_1_titre', label: 'Espace 1 — Titre', contentType: 'title', fallback: 'Espace galerie' },
    { sectionKey: 'espace_1_texte', label: 'Espace 1 — Texte', contentType: 'paragraph', fallback: "Un espace lumineux dédié à la mise en valeur des œuvres, aux accueils sur rendez-vous et aux présentations privées en petit comité." },
    { sectionKey: 'espace_2_titre', label: 'Espace 2 — Titre', contentType: 'title', fallback: 'Pièce esthétique confidentielle' },
    { sectionKey: 'espace_2_texte', label: 'Espace 2 — Texte', contentType: 'paragraph', fallback: 'Une pièce dédiée aux prestations esthétiques et corporelles, organisée de manière professionnelle, calme et discrète.' },
    { sectionKey: 'espace_3_titre', label: 'Espace 3 — Titre', contentType: 'title', fallback: 'Zone technique discrète' },
    { sectionKey: 'espace_3_texte', label: 'Espace 3 — Texte', contentType: 'paragraph', fallback: 'Une zone réservée au rangement du matériel professionnel, sans accueil de clientèle, afin de maintenir un environnement ordonné et fluide.' },
    { sectionKey: 'espaces_note', label: 'Espaces — Note finale', contentType: 'paragraph', fallback: 'Une séparation claire entre les espaces de vie privée et les activités sur rendez-vous.' },
    { sectionKey: 'galerie_eyebrow', label: 'Galerie — Eyebrow', contentType: 'subtitle', fallback: 'Galerie du Laboratoire' },
    { sectionKey: 'galerie_title_1', label: 'Galerie — Titre ligne 1', contentType: 'title', fallback: "L'art devient" },
    { sectionKey: 'galerie_title_2', label: 'Galerie — Titre ligne 2 (italique)', contentType: 'title', fallback: 'expérience' },
    { sectionKey: 'galerie_desc', label: 'Galerie — Description', contentType: 'paragraph', fallback: "Des œuvres choisies pour leur capacité à toucher, éveiller et créer une résonance intérieure.\nChaque exposition est pensée comme une immersion — un moment suspendu entre matière, regard et ressenti." },
    { sectionKey: 'artiste_1_nom', label: 'Artiste 1 — Nom', contentType: 'title', fallback: 'La fée sorcière' },
    { sectionKey: 'artiste_1_discipline', label: 'Artiste 1 — Discipline', contentType: 'subtitle', fallback: 'Terrariums vivants' },
    { sectionKey: 'artiste_1_desc', label: 'Artiste 1 — Description', contentType: 'paragraph', fallback: "Créations organiques et évolutives, où la nature devient œuvre." },
    { sectionKey: 'artiste_2_nom', label: 'Artiste 2 — Nom', contentType: 'title', fallback: 'Alexandra Goris' },
    { sectionKey: 'artiste_2_discipline', label: 'Artiste 2 — Discipline', contentType: 'subtitle', fallback: 'Peintures' },
    { sectionKey: 'artiste_2_desc', label: 'Artiste 2 — Description', contentType: 'paragraph', fallback: "Travail pictural sensible, où couleur et matière ouvrent des espaces intérieurs." },
    { sectionKey: 'artiste_3_nom', label: 'Artiste 3 — Nom', contentType: 'title', fallback: 'Marie-Aude' },
    { sectionKey: 'artiste_3_discipline', label: 'Artiste 3 — Discipline', contentType: 'subtitle', fallback: 'Sculptures' },
    { sectionKey: 'artiste_3_desc', label: 'Artiste 3 — Description', contentType: 'paragraph', fallback: 'Formes incarnées, matière vivante, intuition rendue visible.' },
    { sectionKey: 'cycles_eyebrow', label: 'Cycles — Eyebrow', contentType: 'subtitle', fallback: 'Exposer dans la galerie' },
    { sectionKey: 'cycles_title_1', label: 'Cycles — Titre ligne 1', contentType: 'title', fallback: 'Un cycle, une' },
    { sectionKey: 'cycles_title_2', label: 'Cycles — Titre ligne 2 (italique)', contentType: 'title', fallback: 'présence' },
    { sectionKey: 'cycle_1_label', label: 'Cycle 1 — Libellé', contentType: 'subtitle', fallback: 'Cycle court' },
    { sectionKey: 'cycle_1_duree', label: 'Cycle 1 — Durée', contentType: 'subtitle', fallback: '10 jours' },
    { sectionKey: 'cycle_1_prix', label: 'Cycle 1 — Prix', contentType: 'subtitle', fallback: '400 CHF' },
    { sectionKey: 'cycle_2_label', label: 'Cycle 2 — Libellé', contentType: 'subtitle', fallback: 'Cycle médian' },
    { sectionKey: 'cycle_2_duree', label: 'Cycle 2 — Durée', contentType: 'subtitle', fallback: '20 jours' },
    { sectionKey: 'cycle_2_prix', label: 'Cycle 2 — Prix', contentType: 'subtitle', fallback: '800 CHF' },
    { sectionKey: 'cycle_3_label', label: 'Cycle 3 — Libellé', contentType: 'subtitle', fallback: 'Cycle complet' },
    { sectionKey: 'cycle_3_duree', label: 'Cycle 3 — Durée', contentType: 'subtitle', fallback: '30 jours' },
    { sectionKey: 'cycle_3_prix', label: 'Cycle 3 — Prix', contentType: 'subtitle', fallback: '1200 CHF' },
    { sectionKey: 'cycle_bullet_1', label: 'Cycles — Point 1', contentType: 'paragraph', fallback: 'Accueil de 2 à 3 artistes maximum simultanément.' },
    { sectionKey: 'cycle_bullet_2', label: 'Cycles — Point 2', contentType: 'paragraph', fallback: "Possibilité d'organiser une présentation privée pendant la période d'exposition." },
    { sectionKey: 'cycle_bullet_3', label: 'Cycles — Point 3', contentType: 'paragraph', fallback: 'Événements strictement encadrés sur deux créneaux : 18h00 – 19h30 ou 19h00 – 20h30.' },
    { sectionKey: 'cycle_bullet_4', label: 'Cycles — Point 4', contentType: 'paragraph', fallback: 'Aucun dépassement. Flux maîtrisé, ambiance calme, absence de nuisance.' },
    { sectionKey: 'cycles_cta', label: 'Cycles — Bouton', contentType: 'button', fallback: 'Candidater pour exposer' },
    { sectionKey: 'activites_eyebrow', label: 'Activités — Eyebrow', contentType: 'subtitle', fallback: 'Activités proposées' },
    { sectionKey: 'activites_title_1', label: 'Activités — Titre 1', contentType: 'title', fallback: 'Qualité.' },
    { sectionKey: 'activites_title_2', label: 'Activités — Titre 2 (italique)', contentType: 'title', fallback: 'Discrétion.' },
    { sectionKey: 'activites_title_3', label: 'Activités — Titre 3', contentType: 'title', fallback: 'Maîtrise.' },
    { sectionKey: 'activite_1', label: 'Activité 1', contentType: 'paragraph', fallback: 'Prestations esthétiques haut de gamme — onglerie, soins corporels spécialisés.' },
    { sectionKey: 'activite_2', label: 'Activité 2', contentType: 'paragraph', fallback: 'Épilation et soins avec machine professionnelle.' },
    { sectionKey: 'activite_3', label: 'Activité 3', contentType: 'paragraph', fallback: 'Galerie artistique avec expositions par cycles de 10 à 30 jours.' },
    { sectionKey: 'activite_4', label: 'Activité 4', contentType: 'paragraph', fallback: "Événements privés encadrés — visites, présentations d'artistes, apéritifs en petit comité." },
    { sectionKey: 'activites_note', label: 'Activités — Note finale', contentType: 'paragraph', fallback: 'Chaque prestation est organisée sur rendez-vous afin de garantir qualité, fluidité et absence de nuisance.' },
    { sectionKey: 'events_eyebrow', label: 'Événements — Eyebrow', contentType: 'subtitle', fallback: 'Événements privés' },
    { sectionKey: 'events_title_1', label: 'Événements — Titre ligne 1', contentType: 'title', fallback: 'En' },
    { sectionKey: 'events_title_2', label: 'Événements — Titre ligne 2 (italique)', contentType: 'title', fallback: 'petit comité' },
    { sectionKey: 'events_desc', label: 'Événements — Description', contentType: 'paragraph', fallback: "Visites de galerie, présentations d'artistes, apéritifs en petit comité.\nChaque événement est encadré et confidentiel." },
    { sectionKey: 'events_slot_label', label: 'Événements — Libellé créneaux', contentType: 'subtitle', fallback: 'Créneaux fixes' },
    { sectionKey: 'events_slot_1', label: 'Événements — Créneau 1', contentType: 'subtitle', fallback: '18h00 – 19h30' },
    { sectionKey: 'events_slot_2', label: 'Événements — Créneau 2', contentType: 'subtitle', fallback: '19h00 – 20h30' },
    { sectionKey: 'ctas_title_1', label: 'CTAs — Titre ligne 1', contentType: 'title', fallback: 'Entrer en' },
    { sectionKey: 'ctas_title_2', label: 'CTAs — Titre ligne 2 (italique)', contentType: 'title', fallback: 'lien' },
    { sectionKey: 'cta_1_title', label: 'CTA 1 — Titre', contentType: 'title', fallback: 'Prendre rendez-vous' },
    { sectionKey: 'cta_1_desc', label: 'CTA 1 — Description', contentType: 'paragraph', fallback: "Visite privée, prestation esthétique, présentation d'œuvres." },
    { sectionKey: 'cta_2_title', label: 'CTA 2 — Titre', contentType: 'title', fallback: 'Exposer dans la galerie' },
    { sectionKey: 'cta_2_desc', label: 'CTA 2 — Description', contentType: 'paragraph', fallback: 'Candidater pour un cycle de 10, 20 ou 30 jours.' },
    { sectionKey: 'form_eyebrow', label: 'Formulaire — Eyebrow', contentType: 'subtitle', fallback: 'Galerie du Laboratoire' },
    { sectionKey: 'form_title_1', label: 'Formulaire — Titre ligne 1', contentType: 'title', fallback: 'Candidater pour' },
    { sectionKey: 'form_title_2', label: 'Formulaire — Titre ligne 2 (italique)', contentType: 'title', fallback: 'exposer' },
    { sectionKey: 'form_label_nom', label: 'Formulaire — Label Nom', contentType: 'subtitle', fallback: 'Nom *' },
    { sectionKey: 'form_label_email', label: 'Formulaire — Label Email', contentType: 'subtitle', fallback: 'Email *' },
    { sectionKey: 'form_label_telephone', label: 'Formulaire — Label Téléphone', contentType: 'subtitle', fallback: 'Téléphone' },
    { sectionKey: 'form_label_site', label: 'Formulaire — Label Site', contentType: 'subtitle', fallback: 'Site / portfolio' },
    { sectionKey: 'form_label_univers', label: 'Formulaire — Label Univers', contentType: 'subtitle', fallback: 'Univers artistique *' },
    { sectionKey: 'form_label_description', label: 'Formulaire — Label Description', contentType: 'subtitle', fallback: 'Présentation du projet *' },
    { sectionKey: 'form_placeholder_description', label: 'Formulaire — Placeholder Description', contentType: 'paragraph', fallback: 'Quelques mots sur tes œuvres, ton intention pour cette exposition…' },
    { sectionKey: 'form_label_duree', label: 'Formulaire — Label Durée', contentType: 'subtitle', fallback: 'Durée souhaitée *' },
    { sectionKey: 'form_label_presentation_privee', label: 'Formulaire — Label présentation privée', contentType: 'paragraph', fallback: "Je souhaite organiser une présentation privée pendant la période d'exposition." },
    { sectionKey: 'form_label_creneau', label: 'Formulaire — Label créneau', contentType: 'subtitle', fallback: 'Créneau préféré' },
    { sectionKey: 'form_btn_annuler', label: 'Formulaire — Bouton Annuler', contentType: 'button', fallback: 'Annuler' },
    { sectionKey: 'form_btn_envoyer', label: 'Formulaire — Bouton Envoyer', contentType: 'button', fallback: 'Envoyer la candidature' },
    { sectionKey: 'form_btn_envoi', label: 'Formulaire — Bouton pendant envoi', contentType: 'button', fallback: 'Envoi…' },
    { sectionKey: 'form_note', label: 'Formulaire — Note bas de formulaire', contentType: 'paragraph', fallback: 'Convention de réservation envoyée après validation. Paiement à confirmation.' },
    { sectionKey: 'form_success_title', label: 'Formulaire — Titre succès', contentType: 'title', fallback: 'Candidature reçue.' },
    { sectionKey: 'form_success_text', label: 'Formulaire — Texte succès', contentType: 'paragraph', fallback: 'Une réponse personnelle viendra dans les prochains jours.\nMerci pour ton geste.' },
  ],
  contact: [
    { sectionKey: 'page_title', label: 'Titre de la page', contentType: 'title', fallback: 'Contact' },
    { sectionKey: 'page_intro', label: 'Introduction', contentType: 'paragraph', fallback: 'Un mot, une question, une présence.' },
    { sectionKey: 'email', label: 'Email de contact', contentType: 'paragraph', fallback: 'contact@laboratoire-transformation.fr' },
  ],
}

type FilterMode = 'all' | 'modified' | 'default'

export default function AdminEditor() {
  const { isAuthenticated, isAdmin, user } = useAuth()
  const { items, loading, upsert, remove, fetchAll } = useSiteContent()
  const [selectedPage, setSelectedPage] = useState('accueil')
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [search, setSearch] = useState('')
  const [filterMode, setFilterMode] = useState<FilterMode>('all')
  const [showPreview, setShowPreview] = useState(true)

  // Access control
  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center border border-[#c9a96e]/20 p-12 bg-gradient-to-br from-[#c9a96e]/5 to-transparent">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-[#c9a96e]/40 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-[#c9a96e]" />
          </div>
          <h2 className="font-serif text-3xl text-[#c9a96e] mb-6">Espace réservé</h2>
          <p className="text-[#8a8580] mb-8 font-light leading-relaxed">
            Cet éditeur est accessible uniquement aux administrateurs authentifiés.
          </p>
          <button
            onClick={() => lumi.auth.signIn()}
            className="inline-block px-8 py-3 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#262626] text-sm tracking-widest uppercase transition-all duration-500"
          >
            Se connecter
          </button>
        </div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center border border-[#c9a96e]/20 p-12">
          <h2 className="font-serif text-3xl text-[#c9a96e] mb-6">Accès refusé</h2>
          <p className="text-[#8a8580] mb-4 font-light leading-relaxed">
            Votre compte n'a pas les permissions administrateur.
          </p>
          <p className="text-xs text-[#8a8580]/60 mb-8 font-light">
            Connecté en tant que : {user?.email || user?.userName || user?.userId}
          </p>
          <Link to="/" className="inline-block text-sm tracking-widest uppercase text-[#c9a96e] border-b border-[#c9a96e]/30 pb-2">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    )
  }

  const allSections = SECTION_TEMPLATES[selectedPage] || []

  const existingItem = (sectionKey: string): SiteContentItem | undefined =>
    items.find((i) => i.pageKey === selectedPage && i.sectionKey === sectionKey)

  const getValue = (sectionKey: string, fallback: string) => {
    if (drafts[`${selectedPage}:${sectionKey}`] !== undefined) return drafts[`${selectedPage}:${sectionKey}`]
    const existing = existingItem(sectionKey)
    return existing?.value ?? fallback
  }

  const setValue = (sectionKey: string, value: string) => {
    setDrafts((d) => ({ ...d, [`${selectedPage}:${sectionKey}`]: value }))
  }

  // Filtered sections based on search + filter mode
  const sections = useMemo(() => {
    return allSections.filter((s) => {
      if (search) {
        const q = search.toLowerCase()
        const value = getValue(s.sectionKey, s.fallback).toLowerCase()
        if (!s.label.toLowerCase().includes(q) && !s.sectionKey.toLowerCase().includes(q) && !value.includes(q)) {
          return false
        }
      }
      if (filterMode === 'modified') return !!existingItem(s.sectionKey)
      if (filterMode === 'default') return !existingItem(s.sectionKey)
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSections, search, filterMode, items, drafts])

  const saveSection = async (
    sectionKey: string,
    contentType: SiteContentItem['contentType'],
    fallback: string,
    order: number
  ) => {
    const key = `${selectedPage}:${sectionKey}`
    setSaving(key)
    try {
      const existing = existingItem(sectionKey)
      const value = drafts[key] ?? existing?.value ?? fallback
      await upsert({
        _id: existing?._id,
        pageKey: selectedPage,
        sectionKey,
        contentType,
        value,
        order,
      })
      setDrafts((d) => {
        const next = { ...d }
        delete next[key]
        return next
      })
      window.dispatchEvent(new Event('site-content-updated'))
      setMessage({ type: 'success', text: '✓ Contenu enregistré avec succès' })
      setTimeout(() => setMessage(null), 2500)
    } catch (err: any) {
      console.error(err)
      setMessage({ type: 'error', text: err?.message || "Échec de l'enregistrement" })
    } finally {
      setSaving(null)
    }
  }

  const saveAll = async () => {
    const dirtyKeys = Object.keys(drafts).filter((k) => k.startsWith(`${selectedPage}:`))
    if (dirtyKeys.length === 0) return
    setSaving('__all__')
    try {
      for (const k of dirtyKeys) {
        const sectionKey = k.split(':')[1]
        const section = allSections.find((s) => s.sectionKey === sectionKey)
        if (!section) continue
        const existing = existingItem(sectionKey)
        const idx = allSections.findIndex((s) => s.sectionKey === sectionKey)
        await upsert({
          _id: existing?._id,
          pageKey: selectedPage,
          sectionKey,
          contentType: section.contentType,
          value: drafts[k],
          order: idx,
        })
      }
      setDrafts((d) => {
        const next = { ...d }
        dirtyKeys.forEach((k) => delete next[k])
        return next
      })
      window.dispatchEvent(new Event('site-content-updated'))
      setMessage({ type: 'success', text: `✓ ${dirtyKeys.length} modification(s) enregistrée(s)` })
      setTimeout(() => setMessage(null), 3000)
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Échec' })
    } finally {
      setSaving(null)
    }
  }

  const resetSection = async (sectionKey: string) => {
    const existing = existingItem(sectionKey)
    if (!existing?._id) {
      setDrafts((d) => {
        const next = { ...d }
        delete next[`${selectedPage}:${sectionKey}`]
        return next
      })
      return
    }
    try {
      await remove(existing._id)
      setDrafts((d) => {
        const next = { ...d }
        delete next[`${selectedPage}:${sectionKey}`]
        return next
      })
      window.dispatchEvent(new Event('site-content-updated'))
      setMessage({ type: 'success', text: 'Remis à la valeur par défaut' })
      setTimeout(() => setMessage(null), 2500)
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Échec' })
    }
  }

  const pageStats = useMemo(() => {
    const counts: Record<string, number> = {}
    items.forEach((i) => {
      counts[i.pageKey] = (counts[i.pageKey] || 0) + 1
    })
    return counts
  }, [items])

  const dirtyCount = Object.keys(drafts).filter((k) => k.startsWith(`${selectedPage}:`)).length
  const totalModified = items.length
  const totalSections = Object.values(SECTION_TEMPLATES).reduce((acc, arr) => acc + arr.length, 0)
  const completion = Math.round((totalModified / totalSections) * 100)

  const currentPage = PAGES.find((p) => p.key === selectedPage)

  return (
    <div className="min-h-[100vh] bg-[#262626]">
      {/* Top bar */}
      <div className="sticky top-20 z-40 bg-[#262626]/95 backdrop-blur-xl border-b border-[#c9a96e]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-3 py-2 border border-[#c9a96e]/20 text-xs tracking-widest uppercase text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all"
              >
                <ArrowLeft className="w-3 h-3" /> Site
              </Link>
              <div>
                <h1 className="font-serif text-2xl lg:text-3xl text-[#f5f1ea] leading-tight">
                  Éditeur de <span className="italic text-[#c9a96e]">contenu</span>
                </h1>
                <p className="text-xs text-[#8a8580] font-light mt-0.5">
                  Modifiez, prévisualisez, publiez en temps réel
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {dirtyCount > 0 && (
                <button
                  onClick={saveAll}
                  disabled={saving === '__all__'}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#c9a96e] text-[#262626] hover:bg-[#d4b47a] text-xs tracking-widest uppercase font-semibold transition-all duration-300 disabled:opacity-60"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving === '__all__' ? 'Enregistrement…' : `Enregistrer tout (${dirtyCount})`}
                </button>
              )}
              <button
                onClick={fetchAll}
                disabled={loading}
                className="p-2.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-[#c9a96e] hover:border-[#c9a96e]/50 transition-all"
                title="Rafraîchir"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => lumi.auth.signOut()}
                className="p-2.5 border border-[#c9a96e]/20 text-[#8a8580] hover:text-red-400 hover:border-red-400/50 transition-all"
                title="Se déconnecter"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Message toast */}
      {message && (
        <div className="fixed top-32 right-6 z-50 animate-[slideIn_0.3s_ease-out]">
          <div
            className={`px-6 py-4 border text-sm tracking-wide shadow-2xl ${
              message.type === 'success'
                ? 'border-[#c9a96e]/60 text-[#c9a96e] bg-[#262626] shadow-[#c9a96e]/10'
                : 'border-red-500/60 text-red-400 bg-[#262626] shadow-red-500/10'
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Stats dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="border border-[#c9a96e]/15 p-5 bg-gradient-to-br from-[#c9a96e]/5 to-transparent">
            <p className="text-[10px] tracking-widest uppercase text-[#8a8580] mb-2">Pages</p>
            <p className="font-serif text-3xl text-[#f5f1ea]">{PAGES.length}</p>
          </div>
          <div className="border border-[#c9a96e]/15 p-5 bg-gradient-to-br from-[#c9a96e]/5 to-transparent">
            <p className="text-[10px] tracking-widest uppercase text-[#8a8580] mb-2">Sections totales</p>
            <p className="font-serif text-3xl text-[#f5f1ea]">{totalSections}</p>
          </div>
          <div className="border border-[#c9a96e]/15 p-5 bg-gradient-to-br from-[#c9a96e]/5 to-transparent">
            <p className="text-[10px] tracking-widest uppercase text-[#8a8580] mb-2">Personnalisées</p>
            <p className="font-serif text-3xl text-[#c9a96e]">{totalModified}</p>
          </div>
          <div className="border border-[#c9a96e]/15 p-5 bg-gradient-to-br from-[#c9a96e]/5 to-transparent">
            <p className="text-[10px] tracking-widest uppercase text-[#8a8580] mb-2">Avancement</p>
            <div className="flex items-center gap-3">
              <p className="font-serif text-3xl text-[#f5f1ea]">{completion}%</p>
              <div className="flex-1 h-1 bg-[#c9a96e]/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#c9a96e] to-[#d4b47a] transition-all duration-700"
                  style={{ width: `${completion}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="space-y-2 lg:sticky lg:top-48 lg:self-start">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#8a8580] mb-3 px-2">Pages du site</p>
            {PAGES.map((p) => {
              const Icon = p.icon
              const count = pageStats[p.key] || 0
              const total = SECTION_TEMPLATES[p.key]?.length || 0
              const isSelected = selectedPage === p.key
              return (
                <button
                  key={p.key}
                  onClick={() => {
                    setSelectedPage(p.key)
                    setSearch('')
                    setFilterMode('all')
                  }}
                  className={`w-full text-left px-4 py-3.5 border transition-all duration-300 group ${
                    isSelected
                      ? 'border-[#c9a96e] bg-gradient-to-r from-[#c9a96e]/10 to-transparent'
                      : 'border-[#c9a96e]/10 hover:border-[#c9a96e]/40 hover:bg-[#c9a96e]/5'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-[#c9a96e]' : 'text-[#8a8580] group-hover:text-[#c9a96e]'}`} />
                      <span className={`text-sm tracking-wide ${isSelected ? 'text-[#c9a96e]' : 'text-[#f5f1ea]/80'}`}>
                        {p.label}
                      </span>
                    </div>
                    {count > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[#c9a96e]/15 text-[#c9a96e] tracking-wider">
                        {count}/{total}
                      </span>
                    )}
                  </div>
                </button>
              )
            })}

            <div className="pt-6 mt-6 border-t border-[#c9a96e]/10">
              <a
                href={`/${selectedPage === 'accueil' ? '' : selectedPage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 text-xs tracking-widest uppercase text-[#8a8580] hover:text-[#c9a96e] transition-colors border border-[#c9a96e]/10 hover:border-[#c9a96e]/40"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Voir la page
              </a>
            </div>
          </aside>

          {/* Editor */}
          <section>
            {/* Toolbar */}
            <div className="mb-6 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                {currentPage && <currentPage.icon className="w-5 h-5 text-[#c9a96e]" />}
                <h2 className="font-serif text-2xl text-[#f5f1ea]">
                  {currentPage?.label}
                </h2>
                <span className="text-xs text-[#8a8580] font-light">
                  · {allSections.length} section{allSections.length > 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8580]" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher une section, un texte…"
                    className="w-full bg-[#262626] border border-[#c9a96e]/15 text-[#f5f1ea] pl-11 pr-4 py-3 text-sm font-light focus:border-[#c9a96e] outline-none transition-colors"
                  />
                </div>
                <div className="flex gap-2">
                  {(['all', 'modified', 'default'] as FilterMode[]).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setFilterMode(mode)}
                      className={`px-4 py-3 text-xs tracking-widest uppercase transition-all border ${
                        filterMode === mode
                          ? 'border-[#c9a96e] text-[#c9a96e] bg-[#c9a96e]/5'
                          : 'border-[#c9a96e]/15 text-[#8a8580] hover:text-[#f5f1ea] hover:border-[#c9a96e]/40'
                      }`}
                    >
                      {mode === 'all' ? 'Tous' : mode === 'modified' ? 'Modifiés' : 'Par défaut'}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    className={`px-4 py-3 text-xs tracking-widest uppercase transition-all border flex items-center gap-2 ${
                      showPreview
                        ? 'border-[#c9a96e] text-[#c9a96e] bg-[#c9a96e]/5'
                        : 'border-[#c9a96e]/15 text-[#8a8580] hover:text-[#f5f1ea] hover:border-[#c9a96e]/40'
                    }`}
                    title="Aperçu"
                  >
                    {showPreview ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    <span className="hidden md:inline">Aperçu</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Sections list */}
            <div className="space-y-4">
              {sections.length === 0 && (
                <div className="text-center py-20 border border-dashed border-[#c9a96e]/15">
                  <Search className="w-8 h-8 text-[#8a8580] mx-auto mb-4" />
                  <p className="text-[#8a8580] font-light">Aucune section ne correspond à la recherche</p>
                </div>
              )}

              {sections.map((section, idx) => {
                const key = `${selectedPage}:${section.sectionKey}`
                const currentValue = getValue(section.sectionKey, section.fallback)
                const isDirty = drafts[key] !== undefined && drafts[key] !== (existingItem(section.sectionKey)?.value ?? section.fallback)
                const hasCustom = !!existingItem(section.sectionKey)
                const isSaving = saving === key
                const isLongText = section.contentType === 'paragraph'
                const Icon = CONTENT_TYPE_ICONS[section.contentType] || FileText

                return (
                  <div
                    key={section.sectionKey}
                    className={`border transition-all duration-300 ${
                      isDirty
                        ? 'border-[#c9a96e] shadow-lg shadow-[#c9a96e]/5'
                        : hasCustom
                        ? 'border-[#c9a96e]/30'
                        : 'border-[#c9a96e]/10 hover:border-[#c9a96e]/25'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 p-5 border-b border-[#c9a96e]/10 bg-gradient-to-r from-[#c9a96e]/3 to-transparent">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-9 h-9 flex items-center justify-center border flex-shrink-0 ${
                          hasCustom ? 'border-[#c9a96e]/40 text-[#c9a96e]' : 'border-[#c9a96e]/15 text-[#8a8580]'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-serif text-base text-[#f5f1ea]">{section.label}</h3>
                            <span className="text-[10px] tracking-widest uppercase text-[#c9a96e]/70">
                              {section.contentType}
                            </span>
                          </div>
                          <p className="text-[11px] text-[#8a8580]/70 font-mono truncate">
                            {section.sectionKey}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isDirty && (
                          <span className="text-[10px] px-2 py-1 bg-[#c9a96e] text-[#262626] tracking-widest uppercase font-semibold">
                            Non enregistré
                          </span>
                        )}
                        {hasCustom && !isDirty && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 border border-[#c9a96e]/40 text-[#c9a96e]/90 tracking-widest uppercase">
                            <CheckCircle2 className="w-3 h-3" />
                            Modifié
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Input + preview */}
                    <div className="p-5">
                      {isLongText ? (
                        <textarea
                          value={currentValue}
                          onChange={(e) => setValue(section.sectionKey, e.target.value)}
                          rows={4}
                          className="w-full bg-[#262626] border border-[#c9a96e]/15 text-[#f5f1ea] p-4 font-light leading-relaxed focus:border-[#c9a96e] outline-none transition-colors resize-y text-sm"
                        />
                      ) : (
                        <input
                          type="text"
                          value={currentValue}
                          onChange={(e) => setValue(section.sectionKey, e.target.value)}
                          className="w-full bg-[#262626] border border-[#c9a96e]/15 text-[#f5f1ea] p-4 font-light focus:border-[#c9a96e] outline-none transition-colors"
                        />
                      )}

                      {/* Live preview */}
                      {showPreview && (
                        <div className="mt-4 p-5 border border-[#c9a96e]/10 bg-gradient-to-br from-[#c9a96e]/[0.03] to-transparent">
                          <p className="text-[10px] tracking-widest uppercase text-[#8a8580] mb-3 flex items-center gap-2">
                            <Eye className="w-3 h-3" /> Aperçu
                          </p>
                          {section.contentType === 'title' && (
                            <p className="font-serif text-2xl text-[#f5f1ea] leading-tight">{currentValue}</p>
                          )}
                          {section.contentType === 'subtitle' && (
                            <p className="text-sm tracking-widest uppercase text-[#c9a96e]">{currentValue}</p>
                          )}
                          {section.contentType === 'paragraph' && (
                            <p className="text-[#f5f1ea]/80 font-light leading-relaxed">{currentValue}</p>
                          )}
                          {section.contentType === 'button' && (
                            <span className="inline-block px-6 py-2.5 border border-[#c9a96e] text-[#c9a96e] text-xs tracking-widest uppercase">
                              {currentValue}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between gap-4 mt-5 pt-4 border-t border-[#c9a96e]/10">
                        <p className="text-[11px] text-[#8a8580]/70 font-light italic truncate flex-1">
                          Défaut : <span className="text-[#8a8580]/50">{section.fallback.slice(0, 70)}{section.fallback.length > 70 ? '…' : ''}</span>
                        </p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {hasCustom && (
                            <button
                              onClick={() => resetSection(section.sectionKey)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-widest uppercase text-[#8a8580] hover:text-red-400 border border-[#c9a96e]/15 hover:border-red-400/40 transition-all"
                              title="Remettre par défaut"
                            >
                              <Trash2 className="w-3 h-3" /> Reset
                            </button>
                          )}
                          <button
                            onClick={() => saveSection(section.sectionKey, section.contentType, section.fallback, idx)}
                            disabled={isSaving || !isDirty}
                            className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#262626] text-[10px] tracking-widest uppercase font-semibold transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#c9a96e]"
                          >
                            <Save className="w-3 h-3" /> {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-[#c9a96e]/10">
              <p className="text-xs text-[#8a8580] font-light italic leading-relaxed">
                Les modifications sont enregistrées en base de données et s'appliquent immédiatement au site public.
                Utilisez « Reset » pour revenir au texte par défaut, ou « Enregistrer tout » pour publier toutes vos modifications en une fois.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
